/* eslint-disable consistent-return */
import { useFormik } from "formik";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

import { IcoCheck } from "assets/icons";
import { Button, Input } from "components";
import { HeaderWithBack } from "components/HeaderBack";
import { toast } from "react-toastify";
import { resetAppPassword } from "services/auth";
import { store } from "store";
import styles from "./styles.module.scss";

const PasswordRecoverySchema = Yup.object().shape({
	password: Yup.string()
		.required("Campo obrigatório")
		.min(8, "Campo inválido, verifique as instruções abaixo")
		.matches(/[A-Z]/, "Campo inválido, verifique as instruções abaixo")
		.matches(/[0-9]/, "Campo inválido, verifique as instruções abaixo")
		.matches(/[!@#$%^&*+_=]/, "Campo inválido, verifique as instruções abaixo"),
	passwordConfirmation: Yup.string()
		.required("Campo obrigatório")
		.oneOf(
			[Yup.ref("password")],
			"A confirmação de senha deve ser igual à senha"
		)
});

interface PasswordInstructionProps {
	isValid: boolean;
	label: string;
}
function PasswordInstruction({
	isValid = false,
	label
}: PasswordInstructionProps) {
	return (
		<div className="flex flex-row items-center">
			<IcoCheck color={isValid ? "#5E219C" : "#868F9C"} className="mr-1" />
			<span
				translate="no"
				className={`text-[12px] font-normal leading-none ${
					isValid ? "text-neutral-900" : "text-[#4B5563]"
				} font-poppins`}
			>
				{label}
			</span>
		</div>
	);
}

type PasswordRecoveryFormValues = {
	email: string;
	password: string;
	passwordConfirmation: string;
};

export default function PasswordRecoveryPage() {
	const navigate = useNavigate();
	const registrationInfo = store.getState().registration;

	const [submitting, setSubmitting] = useState<boolean>(false);

	const [passwordRecovery, setPasswordRecovery] =
		useState<PasswordRecoveryFormValues>({
			email: "",
			password: "",
			passwordConfirmation: ""
		});

	const { token } = useParams();

	const formik = useFormik({
		initialValues: passwordRecovery,
		validationSchema: PasswordRecoverySchema,
		enableReinitialize: true,
		onSubmit: async ({ email, password, passwordConfirmation }) => {
			setSubmitting(true);

			await resetAppPassword(token || "", email, password, passwordConfirmation)
				.then(() => {
					setSubmitting(false);

					toast.success("Senha alterada com sucesso!", {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});

					navigate("/login");
				})
				.catch((error) => {
					setSubmitting(false);
					let message = "Algo saiu errado. ";

					if (error.code < 500) {
						message += "Verifique os dados digitados e tente novamente.";
						if (error.body.password)
							formik.errors.password = error.body.password;
						if (error.body.passwordConfirmation)
							formik.errors.passwordConfirmation =
								error.body.passwordConfirmation;
					} else {
						message += "Entre em contato com o suporte.";
					}

					toast.error(message, {
						position: "bottom-center",
						autoClose: 5000,
						hideProgressBar: true,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						progress: undefined,
						theme: "light"
					});
				});
		}
	});

	return (
		<div className={styles.container}>
			<HeaderWithBack label="Alterar senha" backTo={-1} />
			<form onSubmit={formik.handleSubmit} className={styles.form}>
				<div className={styles.container}>
					<Input
						id="email"
						name="email"
						placeholder="Digite seu e-mail aqui"
						label="Email"
						value={formik.values.email || ""}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.email && formik.errors.email}
						errorMessage={formik.errors.email}
						type="email"
					/>
					<Input
						id="password"
						name="password"
						placeholder="Digite sua senha aqui"
						label="SENHA"
						type="password"
						value={formik.values.password}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.password && formik.errors.password}
						errorMessage={formik.errors.password}
					/>
					<div>
						<PasswordInstruction
							isValid={formik.values.password.length >= 8}
							label="A senha deve ter pelo menos 8 caracteres"
						/>
						<PasswordInstruction
							isValid={/[A-Z]/.test(formik.values.password)}
							label="1 letra maiúscula"
						/>
						<PasswordInstruction
							isValid={/[0-9]/.test(formik.values.password)}
							label="1 número"
						/>
						<PasswordInstruction
							isValid={/.*[!@#$%^&*+_=].*/.test(formik.values.password)}
							label="1 caractere especial (! @ # $ % &)"
						/>
					</div>
					<Input
						id="passwordConfirmation"
						name="passwordConfirmation"
						placeholder="Digite sua senha aqui"
						label="REPITA A SENHA"
						type="password"
						value={formik.values.passwordConfirmation}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={
							formik.touched.passwordConfirmation &&
							formik.errors.passwordConfirmation
						}
						errorMessage={formik.errors.passwordConfirmation}
					/>
				</div>
				<div className={styles.buttonWrapper}>
					<Button type="submit">Salvar</Button>
				</div>
			</form>
		</div>
	);
}
